import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink, Eye, ListChecks, RefreshCw, X } from 'lucide-react';
import { GlobalContext } from '../../context/GlobalContext';
import Table from '../../components/Table';
import { Dialog } from '../../components/Dialog';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Notice from '../../components/Notice';
import { Button } from '../../shadcn/Button';
import { callAPI } from '../../hooks/api';

const DEFAULT_DIRECTION = 'egreso';
const MAX_BULK_RECLASSIFICATIONS = 100;

const DIRECTION_OPTIONS = [
	{ label: 'Todos los movimientos', value: 'Todos los movimientos' },
	{ label: 'Ingreso', value: 'ingreso' },
	{ label: 'Egreso', value: 'egreso' }
];

const BANK_OPTIONS = [
	{ label: 'Todos los bancos', value: 'Todos los bancos' },
	{ label: 'BNC', value: 'BNC' },
	{ label: 'Mercantil', value: 'Mercantil' }
];

type MovementRow = { [key: string]: string | number };
type SelectOption = { label: string; value: string };
type CategoryMap = Record<string, string[]>;
type PendingApproval = {
	id: string;
	document_id: string;
	old_category?: string | null;
	new_category: string;
	reason: string;
	requested_by: string;
	requested_at?: string | null;
};

type AIRecommendationGroup = {
	group_key: string;
	transaction_type: string;
	bank: string;
	current_category: string;
	suggested_category: string;
	reason: string;
	evidence: string;
	concept: string;
	count: number;
	total_amount: number;
	avg_confidence?: number | null;
	first_date?: string | null;
	last_date?: string | null;
	job_ids: string[];
	sample_documents: { document_id: string; collection: string; amount?: number | string | null; date?: string | null }[];
};

type AIRecommendationGroupItem = {
	document_id: string;
	collection: string;
	bank: string;
	transaction_type: string;
	date?: string | null;
	amount?: number | string | null;
	account_or_rif?: string | null;
	reference?: string | null;
	concept?: string | null;
	description?: string | null;
	current_category?: string | null;
	suggested_category?: string | null;
	confidence?: number | null;
	reason?: string | null;
	evidence?: string | null;
	job_id?: string | null;
	generated_at?: string | null;
};

type AIRecommendation = {
	document_id: string;
	transaction_type: string;
	amount?: number | null;
	concept?: string | null;
	current_category?: string | null;
	suggested_category: string;
	status?: 'pending' | 'approved' | 'rejected' | null;
	confidence?: number | null;
	reason?: string | null;
	evidence?: string | null;
	job_id?: string | null;
	reviewed_by?: string | null;
	reviewed_at?: string | null;
};

function toSelectOptions(values: string[] = []): SelectOption[] {
	return values.map((value) => ({ label: value.charAt(0).toUpperCase() + value.slice(1), value }));
}

function formatNumber(value: number | null | undefined, maximumFractionDigits = 2) {
	return Number(value || 0).toLocaleString('es-VE', { maximumFractionDigits });
}

function MovementDetailDialog({
	row,
	categories,
	onClose,
	onUpdated
}: {
	row: MovementRow;
	categories: CategoryMap;
	onClose: () => void;
	onUpdated: () => void;
}) {
	const { userData, internalCountry, internalPath } = useContext(GlobalContext);
	const transactionType = String(row['Movimiento'] || DEFAULT_DIRECTION).toLowerCase();
	const bank = String(row['Banco'] || '').toLowerCase();
	const [category, setCategory] = useState(String(row['Categoría'] || ''));
	const [reason, setReason] = useState('');
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [loading, setLoading] = useState(false);

	const categoryOptions = useMemo(() => toSelectOptions(categories[transactionType] || []), [categories, transactionType]);
	const canReclassify = transactionType === 'ingreso' || (transactionType === 'egreso' && bank === 'bnc');

	const handleSubmit = async () => {
		if (!category) {
			setNotice({ message: 'Selecciona una categoría.', variant: 'error' });
			return;
		}
		if (!reason.trim()) {
			setNotice({ message: 'Escribe la razón del cambio.', variant: 'error' });
			return;
		}

		setLoading(true);
		setNotice(null);
		const [response, error] = await callAPI(
			'https://api.krece.app/bank_movement_reclassify/',
			'POST',
			{
				document_id: String(row['Documento'] || ''),
				transaction_type: transactionType,
				bank: String(row['Banco'] || ''),
				category,
				reason: reason.trim()
			},
			userData,
			internalCountry.get()
		);
		setLoading(false);

		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}

		setNotice({ message: response?.status || 'Movimiento reclasificado exitosamente.', variant: 'success' });
		onUpdated();
	};

	return (
		<Dialog title="Detalle del movimiento" onClose={onClose} className="max-h-[90vh] min-w-[min(760px,95vw)] overflow-y-auto">
			<div className="space-y-5">
				<div className="grid gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:grid-cols-2">
					{Object.entries(row).map(([key, value]) => (
						<div key={key} className="min-w-0">
							<p className="text-xs uppercase tracking-wide text-neutral-500">{key}</p>
							<p className="break-words text-sm text-neutral-100">{String(value || '—')}</p>
						</div>
					))}
				</div>

				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
					<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
						<div>
							<h3 className="text-base font-semibold text-neutral-100">Reclasificar categoría</h3>
							<p className="text-sm text-neutral-400">
								Actualiza el movimiento puntual y guarda auditoría con usuario, fecha y razón.
							</p>
						</div>
						<button
							type="button"
							onClick={() => internalPath.set('/bank-account-mappings')}
							className="inline-flex items-center gap-1 text-sm font-medium text-primary-400 hover:text-primary-300"
						>
							Ver mappings/proveedores <ExternalLink className="size-3" />
						</button>
					</div>

					{!canReclassify && (
						<Notice message="Por ahora la reclasificación directa está habilitada para ingresos y egresos BNC." variant="error" />
					)}
					{notice && <Notice message={notice.message} variant={notice.variant} />}

					<div className="mt-4 grid gap-3 md:grid-cols-[1fr_2fr_auto]">
						<Select
							options={categoryOptions}
							defaultValue={category || undefined}
							floatingLabel="Nueva categoría"
							onSelectedChange={(value) => setCategory(typeof value === 'string' ? value : '')}
							disabled={!canReclassify}
						/>
						<Input
							name="reason"
							placeholder="Ej: proveedor mal clasificado"
							floatingLabel="Razón del cambio"
							value={reason}
							onChange={setReason}
							disabled={!canReclassify}
						/>
						<Button disabled={!canReclassify || loading} loading={loading} onClick={handleSubmit} className="!w-auto">
							Guardar
						</Button>
					</div>
				</div>
			</div>
		</Dialog>
	);
}

function BulkReclassificationDialog({
	rows,
	categories,
	onClose,
	onUpdated
}: {
	rows: MovementRow[];
	categories: CategoryMap;
	onClose: () => void;
	onUpdated: (message: string) => void;
}) {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [category, setCategory] = useState('');
	const [reason, setReason] = useState('');
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [loading, setLoading] = useState(false);

	const transactionTypes = useMemo(
		() => [...new Set(rows.map((row) => String(row['Movimiento'] || '').toLowerCase()))],
		[rows]
	);
	const transactionType = transactionTypes.length === 1 ? transactionTypes[0] : '';
	const selectionError = useMemo(() => {
		if (rows.length > MAX_BULK_RECLASSIFICATIONS) {
			return `Puedes reclasificar máximo ${MAX_BULK_RECLASSIFICATIONS} movimientos a la vez.`;
		}
		const hasUnsupportedMovement = rows.some((row) => {
			const rowType = String(row['Movimiento'] || '').toLowerCase();
			const bank = String(row['Banco'] || '').toLowerCase();
			return rowType !== 'ingreso' && !(rowType === 'egreso' && bank === 'bnc');
		});
		if (hasUnsupportedMovement) return 'La selección contiene egresos que no son BNC y no se pueden reclasificar.';
		if (transactionTypes.length !== 1) return 'Selecciona solamente ingresos o solamente egresos para aplicar una categoría común.';
		return null;
	}, [rows, transactionTypes]);
	const categoryOptions = useMemo(() => toSelectOptions(categories[transactionType] || []), [categories, transactionType]);

	const handleSubmit = async () => {
		if (selectionError) return;
		if (!category) {
			setNotice({ message: 'Selecciona una categoría.', variant: 'error' });
			return;
		}
		if (!reason.trim()) {
			setNotice({ message: 'Escribe la razón del cambio.', variant: 'error' });
			return;
		}

		setLoading(true);
		setNotice(null);
		const [response, error] = await callAPI(
			'https://api.krece.app/bank_movement_reclassify/',
			'POST',
			{
				movements: rows.map((row) => ({
					document_id: String(row['Documento'] || ''),
					transaction_type: String(row['Movimiento'] || '').toLowerCase(),
					bank: String(row['Banco'] || '')
				})),
				category,
				reason: reason.trim()
			},
			userData,
			internalCountry.get()
		);
		setLoading(false);

		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}

		onUpdated(response?.status || `${rows.length} movimientos reclasificados exitosamente.`);
	};

	return (
		<Dialog
			title="Reclasificar movimientos seleccionados"
			onClose={onClose}
			cerrarButton={false}
			disableOverlayClose={loading}
			closable={!loading}
			className="min-w-[min(560px,95vw)]"
		>
			<div className="space-y-4">
				<p className="text-sm text-neutral-300">
					Aplicarás la misma categoría y razón a <strong>{rows.length} movimientos</strong>. El cambio quedará registrado en la auditoría de cada movimiento.
				</p>
				{selectionError && <Notice message={selectionError} variant="error" />}
				{notice && <Notice message={notice.message} variant={notice.variant} />}
				<div className="space-y-3">
					<Select
						options={categoryOptions}
						floatingLabel="Nueva categoría"
						onSelectedChange={(value) => setCategory(typeof value === 'string' ? value : '')}
						disabled={Boolean(selectionError) || loading}
					/>
					<Input
						name="bulk-reclassification-reason"
						placeholder="Ej: movimientos de un mismo proveedor mal clasificados"
						floatingLabel="Razón del cambio"
						value={reason}
						onChange={setReason}
						maxLength={500}
						disabled={Boolean(selectionError) || loading}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="secondary" className="!w-auto" onClick={onClose} disabled={loading}>
						Cancelar
					</Button>
					<Button
						variant="primary"
						className="!w-auto"
						Icon={<ListChecks className="size-4" />}
						disabled={Boolean(selectionError)}
						loading={loading}
						onClick={handleSubmit}
					>
						Reclasificar {rows.length} movimientos
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

function ShortDocumentId({ value }: { value: string }) {
	return (
		<span title={value} className="block max-w-[9rem] truncate font-mono text-xs">
			{value}
		</span>
	);
}

function AIRecommendationReviewDialog({
	row,
	onClose,
	onResolve
}: {
	row: { [key: string]: string | number };
	onClose: () => void;
	onResolve: (recommendation: AIRecommendation, action: 'approve' | 'reject') => void;
}) {
	const recommendation: AIRecommendation = {
		document_id: String(row['Documento'] || ''),
		transaction_type: String(row['Movimiento'] || ''),
		amount: Number(row['Monto']) || null,
		concept: String(row['Concepto banco'] || ''),
		current_category: String(row['Categoría actual'] || ''),
		suggested_category: String(row['Categoría sugerida'] || ''),
		status: (String(row['Estado'] || '') as AIRecommendation['status']) || null,
		confidence: Number(row['Confianza']) || null,
		reason: String(row['Razón'] || ''),
		reviewed_by: String(row['Revisado por'] || '')
	};

	return (
		<Dialog title="Revisar recomendación AI" onClose={onClose} className="min-w-[min(640px,95vw)]">
			<div className="space-y-4">
				<div className="grid gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:grid-cols-2">
					{Object.entries(row).map(([key, value]) => (
						<div key={key} className="min-w-0">
							<p className="text-xs uppercase tracking-wide text-neutral-500">{key}</p>
							<p className="break-words text-sm text-neutral-100">{String(value || '—')}</p>
						</div>
					))}
				</div>
				{recommendation.status === 'pending' ? (
					<div className="flex justify-end gap-2">
						<Button variant="primary" className="!w-auto" Icon={<Check className="size-4" />} onClick={() => onResolve(recommendation, 'approve')}>
							Aprobar
						</Button>
						<Button variant="destructive" className="!w-auto" Icon={<X className="size-4" />} onClick={() => onResolve(recommendation, 'reject')}>
							Rechazar
						</Button>
					</div>
				) : (
					<Notice message="Esta recomendación ya fue procesada." variant="success" />
				)}
			</div>
		</Dialog>
	);
}

function AIGroupItemsDialog({ group, onClose }: { group: AIRecommendationGroup; onClose: () => void }) {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [items, setItems] = useState<AIRecommendationGroupItem[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	const fetchItems = useCallback(
		async (cursor?: string | null) => {
			setLoading(true);
			setNotice(null);
			const [res, error] = await callAPI(
				'https://api.krece.app/bank_movement_category_recommendation_groups/items/',
				'GET',
				{
					group_key: group.group_key,
					transaction_type: group.transaction_type,
					limit: 50,
					cursor: cursor || undefined
				},
				userData,
				internalCountry.get()
			);
			setLoading(false);
			if (error) {
				setNotice({ message: error, variant: 'error' });
				return;
			}
			setItems((current) => (cursor ? [...current, ...(res?.items || [])] : res?.items || []));
			setNextCursor(res?.next_cursor || null);
		},
		[group.group_key, group.transaction_type, userData, internalCountry]
	);

	useEffect(() => {
		setItems([]);
		setNextCursor(null);
		fetchItems(null);
	}, [fetchItems]);

	return (
		<Dialog title="Transacciones agrupadas" onClose={onClose} className="max-h-[90vh] min-w-[min(1100px,95vw)] overflow-y-auto">
			<div className="space-y-4">
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3 text-sm text-neutral-300">
					<p><span className="text-neutral-500">Grupo:</span> {formatNumber(group.count, 0)} recomendaciones pendientes</p>
					<p><span className="text-neutral-500">Movimiento:</span> {group.transaction_type || '—'}</p>
					<p><span className="text-neutral-500">Categoría actual:</span> {group.current_category || '—'}</p>
					<p><span className="text-neutral-500">Categoría sugerida:</span> {group.suggested_category || '—'}</p>
					<p><span className="text-neutral-500">Concepto:</span> {group.concept || '—'}</p>
				</div>
				{notice && <Notice message={notice.message} variant={notice.variant} />}
				<div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950">
					<div className="max-h-[380px] overflow-auto">
						<table className="w-full min-w-[1120px] text-left text-sm">
							<thead className="sticky top-0 z-10 bg-neutral-950 text-xs uppercase text-neutral-500">
								<tr>
									<th className="px-3 py-3">Fecha</th>
									<th className="px-3 py-3">Documento</th>
									<th className="px-3 py-3">Monto</th>
									<th className="px-3 py-3">Cuenta/RIF</th>
									<th className="px-3 py-3">Referencia</th>
									<th className="px-3 py-3">Concepto</th>
									<th className="px-3 py-3">Categoría actual</th>
									<th className="px-3 py-3">Categoría sugerida</th>
									<th className="px-3 py-3">Confianza</th>
									<th className="px-3 py-3">Job</th>
								</tr>
							</thead>
							<tbody>
								{items.map((item) => (
									<tr key={`${item.collection}-${item.document_id}`} className="border-t border-neutral-800 text-neutral-200">
										<td className="px-3 py-3">{item.date ? item.date.slice(0, 10) : '—'}</td>
										<td className="px-3 py-3"><ShortDocumentId value={item.document_id} /></td>
										<td className="px-3 py-3">{formatNumber(Number(item.amount) || 0)}</td>
										<td className="px-3 py-3">{item.account_or_rif || '—'}</td>
										<td className="px-3 py-3">{item.reference || '—'}</td>
										<td className="max-w-[260px] truncate px-3 py-3" title={item.concept || item.description || ''}>{item.concept || item.description || '—'}</td>
										<td className="px-3 py-3">{item.current_category || '—'}</td>
										<td className="px-3 py-3">{item.suggested_category || '—'}</td>
										<td className="px-3 py-3">{item.confidence ? `${formatNumber(item.confidence * 100, 1)}%` : '—'}</td>
										<td className="px-3 py-3">{item.job_id || '—'}</td>
									</tr>
								))}
								{loading && (
									<tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-400">Cargando transacciones…</td></tr>
								)}
								{!loading && !items.length && (
									<tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-400">No hay transacciones pendientes para este grupo.</td></tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="secondary" className="!w-auto" onClick={onClose}>Cerrar</Button>
					{nextCursor && (
						<Button variant="secondary" className="!w-auto" onClick={() => fetchItems(nextCursor)} loading={loading}>
							Ver más
						</Button>
					)}
				</div>
			</div>
		</Dialog>
	);
}

function AIRecommendationGroupsPanel({
	groups,
	loading,
	onRefresh,
	onResolve,
	onRecalculate
}: {
	groups: AIRecommendationGroup[];
	loading: boolean;
	onRefresh: () => void;
	onResolve: (group: AIRecommendationGroup, action: 'approve' | 'reject') => void;
	onRecalculate: () => void;
}) {
	const [confirmation, setConfirmation] = useState<{ group: AIRecommendationGroup; action: 'approve' | 'reject' } | null>(null);
	const [recalculateConfirmation, setRecalculateConfirmation] = useState(false);
	const [detailGroup, setDetailGroup] = useState<AIRecommendationGroup | null>(null);

	return (
		<section className="space-y-3">
			{detailGroup && <AIGroupItemsDialog group={detailGroup} onClose={() => setDetailGroup(null)} />}
			{recalculateConfirmation && (
				<Dialog title="Recalcular grupos de recomendaciones" onClose={() => setRecalculateConfirmation(false)} className="min-w-[min(560px,95vw)]">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							Esto encolará un job para reconstruir los grupos desde las recomendaciones AI pendientes.
						</p>
						<p className="text-sm text-neutral-400">
							Úsalo solo si los contadores parecen desincronizados. Puede tardar varios minutos y la tabla seguirá usando el último índice disponible mientras corre.
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setRecalculateConfirmation(false)}>
								Cancelar
							</Button>
							<Button
								variant="primary"
								className="!w-auto"
								Icon={<RefreshCw className="size-4" />}
								onClick={() => {
									onRecalculate();
									setRecalculateConfirmation(false);
								}}
							>
								Confirmar recálculo
							</Button>
						</div>
					</div>
				</Dialog>
			)}
			{confirmation && (
				<Dialog title={confirmation.action === 'approve' ? 'Aprobar grupo de recomendaciones' : 'Rechazar grupo de recomendaciones'} onClose={() => setConfirmation(null)} className="min-w-[min(560px,95vw)]">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							Vas a {confirmation.action === 'approve' ? 'aprobar' : 'rechazar'} un grupo de {formatNumber(confirmation.group.count, 0)} recomendaciones AI pendientes.
						</p>
						<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3 text-sm text-neutral-300">
							<p><span className="text-neutral-500">Movimiento:</span> {confirmation.group.transaction_type || '—'}</p>
							<p><span className="text-neutral-500">Categoría actual:</span> {confirmation.group.current_category || '—'}</p>
							<p><span className="text-neutral-500">Categoría sugerida:</span> {confirmation.group.suggested_category || '—'}</p>
							<p><span className="text-neutral-500">Concepto:</span> {confirmation.group.concept || '—'}</p>
							<p><span className="text-neutral-500">Razón:</span> {confirmation.group.reason || '—'}</p>
						</div>
						<p className="text-sm text-neutral-400">
							Al confirmar, se encolará un job para procesar todo el grupo. En los próximos minutos esas recomendaciones deberían desaparecer de la tabla de pendientes.
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setConfirmation(null)}>
								Cancelar
							</Button>
							<Button
								variant={confirmation.action === 'approve' ? 'primary' : 'destructive'}
								className="!w-auto"
								Icon={confirmation.action === 'approve' ? <Check className="size-4" /> : <X className="size-4" />}
								onClick={() => {
									onResolve(confirmation.group, confirmation.action);
									setConfirmation(null);
								}}
							>
								Confirmar
							</Button>
						</div>
					</div>
				</Dialog>
			)}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-semibold text-neutral-100">Grupos de recomendaciones AI</h2>
					<p className="text-sm text-neutral-400">Aprueba o rechaza miles de recomendaciones iguales en un solo job.</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<Button variant="secondary" className="!w-auto" Icon={<RefreshCw className="size-4" />} onClick={onRefresh} loading={loading}>
						Refrescar grupos
					</Button>
					<Button variant="secondary" className="!w-auto" Icon={<RefreshCw className="size-4" />} onClick={() => setRecalculateConfirmation(true)}>
						Recalcular grupos
					</Button>
				</div>
			</div>
			<div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950">
				<div className="max-h-[380px] overflow-auto">
					<table className="w-full min-w-[1280px] text-left text-sm">
						<thead className="sticky top-0 z-10 bg-neutral-950 text-xs uppercase text-neutral-500">
							<tr>
								<th className="px-3 py-3">Cantidad</th>
								<th className="px-3 py-3">Movimiento</th>
								<th className="px-3 py-3">Categoría actual</th>
								<th className="px-3 py-3">Categoría sugerida</th>
								<th className="px-3 py-3">Concepto</th>
								<th className="px-3 py-3">Razón</th>
								<th className="px-3 py-3">Monto total</th>
								<th className="px-3 py-3">Confianza</th>
								<th className="px-3 py-3 text-right">Acciones</th>
							</tr>
						</thead>
						<tbody>
							{loading && (
								<tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-400">Cargando grupos…</td></tr>
							)}
							{!loading && !groups.length && (
								<tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-400">No hay grupos repetidos para mostrar.</td></tr>
							)}
							{!loading && groups.map((group) => (
								<tr key={group.group_key} className="border-t border-neutral-800 text-neutral-200">
									<td className="px-3 py-3 font-semibold text-neutral-100">{formatNumber(group.count, 0)}</td>
									<td className="px-3 py-3">{group.transaction_type || '—'}</td>
									<td className="px-3 py-3">{group.current_category || '—'}</td>
									<td className="px-3 py-3">{group.suggested_category || '—'}</td>
									<td className="max-w-[280px] truncate px-3 py-3" title={group.concept}>{group.concept || '—'}</td>
									<td className="max-w-[360px] truncate px-3 py-3" title={group.reason}>{group.reason || '—'}</td>
									<td className="px-3 py-3">{formatNumber(group.total_amount)}</td>
									<td className="px-3 py-3">{group.avg_confidence ? `${formatNumber(group.avg_confidence * 100, 1)}%` : '—'}</td>
									<td className="px-3 py-3">
										<div className="flex justify-end gap-2">
											<Button variant="secondary" className="!w-auto" Icon={<Eye className="size-4" />} onClick={() => setDetailGroup(group)}>
												Ver más
											</Button>
											<Button variant="primary" className="!w-auto" Icon={<Check className="size-4" />} onClick={() => setConfirmation({ group, action: 'approve' })}>
												Aprobar grupo
											</Button>
											<Button variant="destructive" className="!w-auto" Icon={<X className="size-4" />} onClick={() => setConfirmation({ group, action: 'reject' })}>
												Rechazar
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}

function AIRecommendationsPanel({
	tableKey,
	categoryOptions,
	onResolve,
	onBulkResolve
}: {
	tableKey: number;
	categoryOptions: SelectOption[];
	onResolve: (recommendation: AIRecommendation, action: 'approve' | 'reject') => void;
	onBulkResolve: (rows: { [key: string]: string | number }[], action: 'approve' | 'reject') => void;
}) {
	const { userData } = useContext(GlobalContext);
	const [reviewRow, setReviewRow] = useState<{ [key: string]: string | number } | null>(null);
	const [selectedRows, setSelectedRows] = useState<{ [key: string]: string | number }[]>([]);
	const [bulkConfirmation, setBulkConfirmation] = useState<{ rows: { [key: string]: string | number }[]; action: 'approve' | 'reject' } | null>(null);
	const pendingSelectedRows = selectedRows.filter((row) => String(row['Estado'] || '') === 'pending');

	if (!userData) return null;

	return (
		<section className="space-y-3">
			{bulkConfirmation && (
				<Dialog title={bulkConfirmation.action === 'approve' ? 'Aprobar recomendaciones seleccionadas' : 'Rechazar recomendaciones seleccionadas'} onClose={() => setBulkConfirmation(null)} className="min-w-[min(520px,95vw)]">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							Vas a {bulkConfirmation.action === 'approve' ? 'aprobar' : 'rechazar'} {bulkConfirmation.rows.length} recomendaciones AI seleccionadas.
						</p>
						<p className="text-sm text-neutral-400">
							Al confirmar, se encolará un job para reclasificar/procesar estos movimientos. En los próximos 5 minutos los movimientos seleccionados deberían desaparecer de esta tabla.
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setBulkConfirmation(null)}>
								Cancelar
							</Button>
							<Button
								variant={bulkConfirmation.action === 'approve' ? 'primary' : 'destructive'}
								className="!w-auto"
								Icon={bulkConfirmation.action === 'approve' ? <Check className="size-4" /> : <X className="size-4" />}
								onClick={() => {
									onBulkResolve(bulkConfirmation.rows, bulkConfirmation.action);
									setBulkConfirmation(null);
								}}
							>
								Confirmar
							</Button>
						</div>
					</div>
				</Dialog>
			)}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-semibold text-neutral-100">Recomendaciones AI pendientes</h2>
					<p className="text-sm text-neutral-400">Sugerencias generadas por el job AI que todavía esperan aprobación o rechazo.</p>
				</div>
				{pendingSelectedRows.length > 0 && (
					<div className="flex flex-wrap gap-2">
						<Button variant="primary" className="!w-auto" Icon={<Check className="size-4" />} onClick={() => setBulkConfirmation({ rows: pendingSelectedRows, action: 'approve' })}>
							Aprobar seleccionadas ({pendingSelectedRows.length})
						</Button>
						<Button variant="destructive" className="!w-auto" Icon={<X className="size-4" />} onClick={() => setBulkConfirmation({ rows: pendingSelectedRows, action: 'reject' })}>
							Rechazar seleccionadas ({pendingSelectedRows.length})
						</Button>
					</div>
				)}
			</div>
			{reviewRow && <AIRecommendationReviewDialog row={reviewRow} onClose={() => setReviewRow(null)} onResolve={onResolve} />}
			<Table
				key={tableKey}
				className="h-[500px]"
				fillHeight
				userEmail={null}
				endpoint="https://api.krece.app/tables/bank_movement_category_recommendations/"
				orderBy={{ columnName: 'Estado', columnOrder: 'ASC' }}
				pagination={{ default: 50, batchSizes: [20, 50, 100] }}
				hideDateFilters
				hideExport
				firstColumns={['Fecha', 'Documento', 'Movimiento', 'Monto', 'Cuenta/RIF', 'Concepto banco']}
				truncateColumns={{ Documento: 140, 'Cuenta/RIF': 180, 'Concepto banco': 260, Razón: 360 }}
				search_by={[
					{ column: 'Monto', placeholder: 'Buscar por monto exacto...', type: 'numeric', label: 'Monto' },
					{ column: 'Cuenta/RIF', placeholder: 'Buscar RIF/cédula/cuenta...', type: 'alphanumeric', label: 'RIF/cuenta' },
					{ column: 'Referencia', placeholder: 'Buscar referencia...', type: 'alphanumeric', label: 'Referencia' },
					{ column: 'Concepto banco', placeholder: 'Buscar concepto del banco...', type: 'alphanumeric', label: 'Concepto banco' },
					{ column: 'Descripción', placeholder: 'Buscar descripción...', type: 'alphanumeric', label: 'Descripción' },
					{ column: 'Documento', placeholder: 'Buscar document_id...', type: 'alphanumeric', label: 'Documento' }
				]}
				filters={[
					{ column: 'Banco', options: BANK_OPTIONS, defaultValue: 'Todos los bancos' },
					{ column: 'Movimiento', options: DIRECTION_OPTIONS, defaultValue: 'Todos los movimientos' },
					{ column: 'Categoría', options: categoryOptions, defaultValue: 'Todas las categorías' }
				]}
				selectable={{ idColumn: 'Documento', onSelectionChange: setSelectedRows }}
				actionButton={{ label: 'Revisar', onClick: setReviewRow }}
			/>
		</section>
	);
}

function PendingApprovalsPanel({ approvals, onResolve }: { approvals: PendingApproval[]; onResolve: (id: string, action: 'approve' | 'reject') => void }) {
	if (!approvals.length) return null;

	return (
		<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
			<div className="mb-3">
				<h2 className="text-base font-semibold text-neutral-100">Cambios pendientes de aprobación</h2>
				<p className="text-sm text-neutral-400">Solicitudes creadas por Finanzas que requieren aprobación admin.</p>
			</div>
			<div className="max-h-[380px] overflow-auto">
				<table className="w-full min-w-[1040px] text-left text-sm">
					<thead className="text-xs uppercase text-neutral-500">
						<tr>
							<th className="px-2 py-2">Documento</th>
							<th className="px-2 py-2">Categoría actual</th>
							<th className="px-2 py-2">Nueva categoría</th>
							<th className="px-2 py-2">Solicitado por</th>
							<th className="px-2 py-2">Razón</th>
							<th className="px-2 py-2 text-right">Acciones</th>
						</tr>
					</thead>
					<tbody>
						{approvals.map((approval) => (
							<tr key={approval.id} className="border-t border-neutral-800 text-neutral-200">
								<td className="px-2 py-2"><ShortDocumentId value={approval.document_id} /></td>
								<td className="px-2 py-2">{approval.old_category || '—'}</td>
								<td className="px-2 py-2">{approval.new_category}</td>
								<td className="px-2 py-2">{approval.requested_by}</td>
								<td className="px-2 py-2">{approval.reason}</td>
								<td className="px-2 py-2">
									<div className="flex justify-end gap-2">
										<Button variant="primary" className="!w-auto" Icon={<Check className="size-4" />} onClick={() => onResolve(approval.id, 'approve')}>
											Aprobar
										</Button>
										<Button variant="destructive" className="!w-auto" Icon={<X className="size-4" />} onClick={() => onResolve(approval.id, 'reject')}>
											Rechazar
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export default function Conciliaciones() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [tableKey, setTableKey] = useState(Math.random());
	const [aiTableKey, setAITableKey] = useState(Math.random());
	const [selectedRow, setSelectedRow] = useState<MovementRow | null>(null);
	const [selectedMovements, setSelectedMovements] = useState<MovementRow[]>([]);
	const [bulkReclassificationOpen, setBulkReclassificationOpen] = useState(false);
	const [categories, setCategories] = useState<CategoryMap>({});
	const [selectedDirection, setSelectedDirection] = useState<string>('Todos los movimientos');
	const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
	const [aiGroups, setAIGroups] = useState<AIRecommendationGroup[]>([]);
	const [aiGroupsLoading, setAIGroupsLoading] = useState(false);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const canApprove = userData?.access_type === 'admin' || userData?.access_type === 'admin_finanzas';

	useEffect(() => {
		(async () => {
			const [res] = await callAPI('https://api.krece.app/bank_account_mappings/', 'GET', undefined, userData, internalCountry.get());
			if (res?.valid_categories_by_direction) setCategories(res.valid_categories_by_direction);
		})();
	}, [userData, internalCountry]);

	const fetchPendingApprovals = useCallback(async () => {
		if (!canApprove) return;
		const [res] = await callAPI('https://api.krece.app/bank_movement_category_approvals/', 'GET', undefined, userData, internalCountry.get());
		setPendingApprovals(res?.approvals || []);
	}, [canApprove, userData, internalCountry]);

	useEffect(() => {
		fetchPendingApprovals();
	}, [fetchPendingApprovals]);

	const fetchAIGroups = useCallback(async () => {
		if (!canApprove) return;
		setAIGroupsLoading(true);
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_recommendation_groups/',
			'GET',
			{ min_count: 2, limit: 100 },
			userData,
			internalCountry.get()
		);
		setAIGroupsLoading(false);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setAIGroups(res?.groups || []);
	}, [canApprove, userData, internalCountry]);

	useEffect(() => {
		fetchAIGroups();
	}, [fetchAIGroups]);

	const handleRecalculateAIGroups = async () => {
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_recommendation_groups/',
			'POST',
			{},
			userData,
			internalCountry.get()
		);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setNotice({ message: res?.status || 'Recálculo de grupos encolado.', variant: 'success' });
		setTimeout(fetchAIGroups, 30 * 1000);
	};

	const handleResolveAIGroup = async (group: AIRecommendationGroup, action: 'approve' | 'reject') => {
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_recommendation_groups/resolve/',
			'POST',
			{
				action,
				group_key: group.group_key,
				group_payload: {
					transaction_type: group.transaction_type,
					bank: group.bank,
					current_category: group.current_category,
					suggested_category: group.suggested_category,
					reason: group.reason,
					evidence: group.evidence,
					concept: group.concept,
					count: group.count
				}
			},
			userData,
			internalCountry.get()
		);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setNotice({
			message: `${res?.status || 'Job de grupo encolado'} para ${formatNumber(group.count, 0)} recomendaciones. En los próximos minutos deberían desaparecer de pendientes.`,
			variant: 'success'
		});
		fetchAIGroups();
		setAITableKey(Math.random());
		setTimeout(() => {
			fetchAIGroups();
			setAITableKey(Math.random());
		}, 5 * 60 * 1000);
	};

	const handleResolveRecommendation = async (recommendation: AIRecommendation, action: 'approve' | 'reject') => {
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_recommendations/',
			'POST',
			{ document_id: recommendation.document_id, transaction_type: recommendation.transaction_type, action },
			userData,
			internalCountry.get()
		);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setNotice({ message: res?.status || 'Recomendación procesada.', variant: 'success' });
		setAITableKey(Math.random());
		setTableKey(Math.random());
	};

	const handleBulkResolveRecommendations = async (rows: { [key: string]: string | number }[], action: 'approve' | 'reject') => {
		const recommendations = rows.map((row) => ({
			document_id: String(row['Documento'] || ''),
			transaction_type: String(row['Movimiento'] || '')
		}));
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_recommendations/bulk/',
			'POST',
			{ action, recommendations },
			userData,
			internalCountry.get()
		);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setNotice({
			message: `${res?.status || 'Job encolado'} (${res?.total_requested || recommendations.length} recomendaciones). En los próximos 5 minutos los movimientos seleccionados deberían desaparecer de la tabla.`,
			variant: 'success'
		});
		fetchAIGroups();
		setAITableKey(Math.random());
		setTimeout(() => {
			fetchAIGroups();
			setAITableKey(Math.random());
		}, 5 * 60 * 1000);
	};

	const handleResolveApproval = async (approvalId: string, action: 'approve' | 'reject') => {
		const [res, error] = await callAPI(
			'https://api.krece.app/bank_movement_category_approvals/',
			'POST',
			{ approval_id: approvalId, action },
			userData,
			internalCountry.get()
		);
		if (error) {
			setNotice({ message: error, variant: 'error' });
			return;
		}
		setNotice({ message: res?.status || 'Solicitud procesada.', variant: 'success' });
		fetchPendingApprovals();
		setTableKey(Math.random());
	};

	const categoryOptions = useMemo(() => {
		const availableCategories = categories[selectedDirection] || Object.values(categories).flat();
		const uniqueCategories = [...new Set(availableCategories)].sort();
		return [{ label: 'Todas las categorías', value: 'Todas las categorías' }, ...toSelectOptions(uniqueCategories)];
	}, [categories, selectedDirection]);

	if (!userData) return null;

	return (
		<div className="min-h-full w-full space-y-8 p-4 md:p-6">
			<div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
				<div>
					<h1 className="text-lg font-semibold text-neutral-100">Conciliaciones</h1>
					<p className="text-sm text-neutral-400">
						Busca movimientos bancarios sin cargar Firestore masivamente. Usa mappings/proveedores existentes para reglas recurrentes.
					</p>
				</div>
				<Button variant="secondary" className="!w-auto" Icon={<RefreshCw className="size-4" />} onClick={() => setTableKey(Math.random())}>
					Refrescar
				</Button>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}
			{bulkReclassificationOpen && (
				<BulkReclassificationDialog
					rows={selectedMovements}
					categories={categories}
					onClose={() => setBulkReclassificationOpen(false)}
					onUpdated={(message) => {
						setNotice({ message, variant: 'success' });
						setBulkReclassificationOpen(false);
						setSelectedMovements([]);
						setTableKey(Math.random());
						setAITableKey(Math.random());
						fetchPendingApprovals();
					}}
				/>
			)}
			{selectedRow && (
				<MovementDetailDialog
					row={selectedRow}
					categories={categories}
					onClose={() => setSelectedRow(null)}
					onUpdated={() => {
						setTableKey(Math.random());
						setAITableKey(Math.random());
						fetchPendingApprovals();
					}}
				/>
			)}
			{canApprove && <PendingApprovalsPanel approvals={pendingApprovals} onResolve={handleResolveApproval} />}

			<section className="space-y-3">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<h2 className="text-lg font-semibold text-neutral-100">Movimientos bancarios</h2>
						<p className="text-sm text-neutral-400">Busca ingresos y egresos por fecha, monto, banco, referencia, concepto, descripción o cuenta/RIF.</p>
					</div>
					{selectedMovements.length > 0 && (
						<Button
							variant="primary"
							className="!w-auto"
							Icon={<ListChecks className="size-4" />}
							onClick={() => setBulkReclassificationOpen(true)}
						>
							Reclasificar seleccionados ({selectedMovements.length})
						</Button>
					)}
				</div>
				<Table
					key={tableKey}
					className="h-[500px]"
					fillHeight
					userEmail={null}
					endpoint="https://api.krece.app/tables/bank_movements_conciliation/"
					orderBy={{ columnName: 'Fecha', columnOrder: 'DESC' }}
					pagination={{ default: 50, batchSizes: [20, 50, 100] }}
					firstColumns={['Fecha', 'Banco', 'Movimiento', 'Monto', 'Monto USD', 'Categoría', 'Referencia', 'Cuenta/RIF']}
					truncateColumns={{ Descripción: 80, Concepto: 60 }}
					search_by={[
						{ column: 'Monto', placeholder: 'Buscar por monto exacto...', type: 'numeric', label: 'Monto' },
						{ column: 'Cuenta/RIF', placeholder: 'Buscar RIF/cédula/cuenta...', type: 'alphanumeric', label: 'RIF/cuenta' },
						{ column: 'Referencia', placeholder: 'Buscar referencia...', type: 'alphanumeric', label: 'Referencia' },
						{ column: 'Concepto', placeholder: 'Buscar concepto del banco...', type: 'alphanumeric', label: 'Concepto banco' },
						{ column: 'Descripción', placeholder: 'Buscar descripción...', type: 'alphanumeric', label: 'Descripción' },
						{ column: 'Documento', placeholder: 'Buscar document_id...', type: 'alphanumeric', label: 'Documento' }
					]}
					filters={[
						{ column: 'Banco', options: BANK_OPTIONS, defaultValue: 'Todos los bancos' },
						{ column: 'Movimiento', options: DIRECTION_OPTIONS, defaultValue: 'Todos los movimientos' },
						{ column: 'Categoría', options: categoryOptions, defaultValue: 'Todas las categorías' }
					]}
					onFilterChange={(column, value) => {
						if (column === 'Movimiento') setSelectedDirection(typeof value === 'string' ? value : 'Todos los movimientos');
					}}
					selectable={{ idColumn: 'Documento', onSelectionChange: setSelectedMovements }}
					actionButton={{ label: 'Detalle', onClick: setSelectedRow }}
					exportLimit={50000}
					allowExportWithoutTotal
				/>
			</section>

			{canApprove && (
				<AIRecommendationGroupsPanel
					groups={aiGroups}
					loading={aiGroupsLoading}
					onRefresh={fetchAIGroups}
					onResolve={handleResolveAIGroup}
					onRecalculate={handleRecalculateAIGroups}
				/>
			)}
			{canApprove && (
				<AIRecommendationsPanel
					tableKey={aiTableKey}
					categoryOptions={categoryOptions}
					onResolve={handleResolveRecommendation}
					onBulkResolve={handleBulkResolveRecommendations}
				/>
			)}
		</div>
	);
}
